/* eslint-disable @next/next/no-img-element */
import React, { useRef, useState, useEffect, useContext } from "react";
import { Button } from "../../../../shadcn/components/ui/button";
import { UpcomingAuctionCard } from "../../../../shadcn/components/custom/UpcomingAuctionCard";
import { Context, getSearchResults, getWatermarkedImgixUrl } from "services";
import { DateTime } from "luxon";
import { useRouter } from "next/router";

interface UpcomingAuctionItems {
  title?: string;
  price?: string;
  address?: string;
  imageLink?: string;
  auctionDate?: string;
  bankName?: string;
  bankLogo?: React.ReactNode;
  propertyLink?: string;
  propertyDateType?: string;
}

export default function UpcomingAuction() {
  const router = useRouter();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { searchForm }: any = useContext(Context);
  const [upcomingAuction, setUpcomingAuction] = useState<
    UpcomingAuctionItems[]
  >([]);
  const [isPropertyFoundInCurCity, setIsPropertyFoundInCurCity] =
    useState<boolean>(false);

  const [hasFetched, setHasFetched] = useState(false);

  const init = async () => {
    const startUnix = DateTime.now()
      .setZone("Asia/Kolkata")
      .plus({ days: 1 })
      .startOf("day")
      .toSeconds();
    console.log("startUnix: ", startUnix);
    const data: any = await getSearchResults({
      searches: [
        {
          collection: "properties",
          q: "*",
          query_by: "",
          // facet_by: "dump.location.locality_slug",
          // facet_return_parent: "dump.location.locality_slug",
          filter: "true",
          filter_by: `status:active && property_date_type:=undated && dump.location.city_name:${searchForm.value.city.label}`,
          max_facet_values: "100",
          per_page: 4,
          sort_by: "auction_start_date_unix_timestamp:asc",
        },
        {
          collection: "properties",
          q: "*",
          query_by: "",
          // facet_by: "dump.location.locality_slug",
          // facet_return_parent: "dump.location.locality_slug",
          filter: "true",
          filter_by: `status:active && property_date_type:=undated`,
          max_facet_values: "100",
          per_page: 8,
          sort_by: "auction_start_date_unix_timestamp:asc",
        },
        {
          collection: "properties",
          q: "*",
          query_by: "",
          filter: "true",
          filter_by: `status:active && property_date_type:=dated && auction_start_date_unix_timestamp:>${startUnix}000`,
          max_facet_values: "100",
          per_page: 4,
          sort_by: "auction_start_date_unix_timestamp:asc",
        },
      ],
    });
    const allUpcomingAuctionProperties =
      [
        ...(data.results[0]?.hits || []),
        ...(data.results[1]?.hits || []),
        ...(data.results[2]?.hits || []),
      ].map((hit: any) => ({
        title: hit.document.borrower_name ? hit.document.borrower_name : "",
        price: hit.document.reserve_price,
        listing_id: hit.document.listing_id,
        address: hit.document?.address?.split(" ").slice(0, 10).join(" "),
        imageLink: hit.document?.dump?.property_image
          ? getWatermarkedImgixUrl(
              `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${hit.document?.dump?.property_image}`,
            )
          : hit.document?.dump?.asset?.placeholder_image
          ? getWatermarkedImgixUrl(
              `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${hit?.document?.dump?.asset?.placeholder_image}`,
            )
          : "/ShortListedProperty.png",
        auctionDate: DateTime.fromISO(hit.document.auction_start_date_time)
          .setZone("Asia/Kolkata")
          .toFormat("dd MMMM yyyy"),
        bankName: hit?.document?.dump?.bank?.bank_name,
        bankLogo: hit?.document?.dump?.bank?.bank_logo ? (
          <img
            src={`${process.env.NEXT_PUBLIC_IMAGIX_URL}/${hit.document.dump.bank.bank_logo}?w=22&h=22&dpr=2&auto=format`}
            alt=""
          />
        ) : (
          <p className="bg-brand-orange rounded-full flex justify-center items-center h-6 w-6 font-openSans font-semibold uppercase text-web-body-sm leading-4 text-white py-2">
            {hit?.document?.dump?.bank?.bank_name?.charAt(0)}
          </p>
          // <HDFCLogo height="22px" width="22px" />
        ),
        propertyLink: `/auction-properties/${hit.document.listing_id}`,
        propertyDateType: hit.document.property_date_type,
      })) || [];

    const newUpcomingAuctionProperties = allUpcomingAuctionProperties.filter(
      (prop, index, self) =>
        prop.listing_id &&
        index === self.findIndex((p) => p.listing_id === prop.listing_id),
    );

    setUpcomingAuction(newUpcomingAuctionProperties);
    if (data.results[0]?.hits && data.results[0]?.hits?.length > 0) {
      setIsPropertyFoundInCurCity(true);
    } else if (data.results[2]?.hits?.length > 0) {
      setHasFetched(true);
    } else {
      setIsPropertyFoundInCurCity(false);
    }
  };

  useEffect(() => {
    (async () => {
      await init();
    })();
  }, [searchForm.value?.city?.value]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const updateScrollPosition = () => {
      const scrollPercentage =
        2.3 *
        (scrollContainer.scrollLeft /
          (scrollContainer.scrollWidth - scrollContainer.clientWidth)) *
        100;
      scrollContainer.style.setProperty(
        "--scroll-position",
        `${scrollPercentage}%`,
      );
    };

    scrollContainer.addEventListener("scroll", updateScrollPosition);
    return () =>
      scrollContainer.removeEventListener("scroll", updateScrollPosition);
  }, []);
  return (
    <div className="lg:flex ">
      <div className="w-full p-7  lg:px-customPadding mt-[15px] lg:mt-20  lg:pb-[53px] 2xl:w-[1340px] 2xl:mx-auto 2xl:px-0">
        <div className="px-4 py-6 lg:px-[100px] lg:py-20 lg:bg-[#F8F8F8]">
          {/* Heading */}
          <div className="flex items-center">
            <p className="font-spaceGrotesk font-normal text-mobile-h2 leading-[35px] text-brand-textBlack lg:text-web-h2 lg:leading-[57px]">
              PROPERTIES FOR UPCOMING AUCTION
            </p>
          </div>
          {/* Cards */}
          <div className="relative">
            <div
              ref={scrollContainerRef}
              className="flex overflow-x-auto lg:overflow-hidden w-full mt-8 lg:w-full lg:grid lg:grid-cols-2 lg:gap-[52px] lg:mt-[70px] custom-scrollbar"
            >
              {upcomingAuction.slice(0, 4).map((item: any, index: number) => (
                <UpcomingAuctionCard
                  key={`upcoming_prop_${index}`}
                  title={item?.title}
                  price={item?.price}
                  address={item?.address}
                  imageLink={item?.imageLink}
                  auctionDate={item?.auctionDate}
                  bankName={item?.bankName}
                  bankLogo={item?.bankLogo}
                  propertyLink={item?.propertyLink}
                  propertyDateType={item?.propertyDateType}
                  className="mr-1 md:mr-6 lg:mr-0"
                />
              ))}
              <div className="lg:hidden absolute custom-scrollbar-track translate-y-4" />
            </div>
            {/* Right blur effect */}
            <div className="lg:hidden absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-white to-transparent pointer-events-none"></div>
          </div>

          {/* Button */}
          <div className="hidden lg:flex mt-10 justify-center">
            <Button
              onClick={() => {
                if (isPropertyFoundInCurCity) {
                  searchForm.setField("asset_type", "");
                  router.push(
                    `/auction-properties-in-${searchForm.value.city.value}?filter_by=undated`,
                  );
                } else if (hasFetched) {
                  searchForm.setField("city", {
                    label: "All Cities",
                    value: "",
                  });
                  searchForm.setField("asset_type", "");
                  router.push(`/auction-properties`);
                } else {
                  searchForm.setField("city", {
                    label: "All Cities",
                    value: "",
                  });
                  searchForm.setField("asset_type", "");
                  router.push(`/auction-properties?filter_by=undated`);
                }
              }}
              className=" h-[60px] w-[187px] justify-center"
            >
              <p className="font-openSans font-semibold text-[22px] leading-[30px] text-brand-textWhite">
                View all
              </p>
            </Button>
          </div>

          {/* Reason */}
          <div className="mt-10 pr-[15px]  lg:pr-0 space-y-10 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-10 lg:mt-[58px]">
            <div className="w-full lg:max-w-[236px]">
              <div className="font-openSans font-semibold text-mobile-body-lg lg:text-[20px] leading-[22px] lg:leading-[39px] text-brand-textGrey tracking-wider">
                01
              </div>
              <div className="font-openSans font-normal leading-[23px] text-brand-textBlack text-mobile-subheading lg:text-[22px] mt-3 lg:mt-4">
                Cost Benefit
              </div>
              <div className="font-openSans font-normal text-mobile-body-sm leading-5 lg:leading-[23px] text-brand-textGrey lg:text-web-body-lg mt-1 lg:mt-[18px]">
                Bank auction properties are potentially 20-30% cheaper than the
                prevailing market price
              </div>
            </div>
            <div className="w-full lg:max-w-[236px]">
              <div className="font-openSans font-semibold text-mobile-body-lg lg:text-[20px] leading-[22px] lg:leading-[39px] text-brand-textGrey tracking-wider">
                02
              </div>
              <div className="font-openSans font-normal leading-[23px] text-brand-textBlack text-mobile-subheading lg:text-[22px] mt-3 lg:mt-4">
                Credible Sellers
              </div>
              <div className="font-openSans font-normal text-mobile-body-sm leading-5 lg:leading-[23px] text-brand-textGrey lg:text-web-body-lg mt-1 lg:mt-[18px]">
                The bank/financial institutions are authorized by Govt. of
                India. Hence it is safe to purchase such properties.
              </div>
            </div>
            <div className="w-full lg:max-w-[236px]">
              <div className="font-openSans font-semibold text-mobile-body-lg lg:text-[20px] leading-[22px] lg:leading-[39px] text-brand-textGrey tracking-wider">
                03
              </div>
              <div className="font-openSans font-normal leading-[23px] text-brand-textBlack text-mobile-subheading lg:text-[22px] mt-3 lg:mt-4">
                Hassle-Free Process
              </div>
              <div className="font-openSans font-normal text-mobile-body-sm leading-5 lg:leading-[23px] text-brand-textGrey lg:text-web-body-lg  mt-1 lg:mt-[18px]">
                The buyer will have a lesser burden of going through the legal
                liabilities of the property.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
